import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, DollarSign, Ticket, ShoppingBag, QrCode, CheckCircle2, XCircle, Trash2, Plus, Trophy, Users2, Megaphone, Image as ImageIcon, Settings, UserCheck, Copy, Pencil, KeyRound, Wallet, Banknote, Camera, UploadCloud, Link2, UserPlus, Send, BellRing, Grid3x3, Video, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ShareMenu from "@/components/ShareMenu";
import { BoardShareImage } from "@/components/BoardShareImage";
import { formatDate } from "@/components/EventCard";

const TABS = [
  { id: "overview", label: "Sales", icon: DollarSign },
  { id: "referral", label: "My Link", icon: Link2 },
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "checkin", label: "Check-In", icon: QrCode },
  { id: "applications", label: "Applications", icon: UserCheck },
  { id: "members", label: "Members", icon: Users2 },
  { id: "payouts", label: "Payouts", icon: Wallet },
  { id: "rewards", label: "Rewards", icon: Trophy },
  { id: "events", label: "Events", icon: Ticket },
  { id: "squares", label: "Board", icon: Grid3x3 },
  { id: "merch", label: "Shop", icon: ShoppingBag },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "announcements", label: "News", icon: Megaphone },
  { id: "content", label: "Content", icon: Settings },
  { id: "notify", label: "Notify", icon: BellRing },
  { id: "account", label: "Password", icon: KeyRound },
];

const TAB_GROUPS = [
  { label: "Sell", ids: ["overview", "referral", "orders", "checkin"] },
  { label: "People", ids: ["applications", "members", "payouts", "rewards", "notify", "account"] },
  { label: "Content", ids: ["events", "squares", "merch", "gallery", "announcements", "content"] },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#e0481f]" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <p className="eyebrow text-[#e0481f] text-xs mb-1">Admin Dashboard</p>
      <h1 className="headline text-4xl sm:text-5xl text-[#f7f3ec] mb-6">Ryders Control</h1>

      <div className="mb-8 space-y-2.5" data-testid="admin-tab-strip">
        {TAB_GROUPS.map((grp) => (
          <div key={grp.label} className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#6b645b] w-full sm:w-16 shrink-0">{grp.label}</span>
            {grp.ids.map((id) => {
              const t = TABS.find((x) => x.id === id);
              const Icon = t.icon;
              return (
                <button key={id} onClick={() => setTab(id)} className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 font-display uppercase tracking-wide text-xs transition-colors ${tab === id ? "btn-gold" : "gold-border text-[#d6cec2]"}`} data-testid={`admin-tab-${id}`}>
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "referral" && <AdminReferral user={user} />}
      {tab === "orders" && <Orders />}
      {tab === "checkin" && <CheckIn />}
      {tab === "applications" && <Applications />}
      {tab === "members" && <MembersAdmin />}
      {tab === "payouts" && <PayoutsAdmin />}
      {tab === "rewards" && <RewardsAdmin />}
      {tab === "events" && <EventsAdmin />}
      {tab === "squares" && <SquaresAdmin />}
      {tab === "merch" && <MerchAdmin />}
      {tab === "gallery" && <GalleryAdmin />}
      {tab === "announcements" && <AnnouncementsAdmin />}
      {tab === "content" && <ContentAdmin />}
      {tab === "notify" && <NotifyAdmin />}
      {tab === "account" && <AdminAccount user={user} />}
    </div>
  );
}

function AdminAccount({ user }) {
  const [pw, setPw] = useState({ current: "", next: "" });
  const save = async () => {
    if (!pw.current || !pw.next) { toast.error("Fill in both password fields"); return; }
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated!");
      setPw({ current: "", next: "" });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="card-warm rounded-2xl p-6 max-w-xl" data-testid="admin-change-password">
      <h2 className="headline text-2xl text-[#f7f3ec] mb-1 flex items-center gap-2"><KeyRound size={20} className="text-[#e0481f]" /> Login & Password</h2>
      <p className="text-[#a9a196] text-sm mb-4">You log in with <span className="text-[#f3c649]">{user.email}</span>. Change your password anytime.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} placeholder="Current password" className="bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="admin-pw-current" />
        <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="New password" className="bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="admin-pw-new" />
      </div>
      <button onClick={save} className="mt-3 btn-ember rounded-full px-6 py-3 font-display uppercase tracking-wide inline-flex items-center gap-2" data-testid="admin-pw-save"><KeyRound size={16} /> Update Password</button>
    </div>
  );
}

function NotifyAdmin() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("members");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const load = useCallback(() => api.get("/admin/notifications").then((r) => setHistory(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Add a title and a message"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications", { title, body, audience });
      toast.success(`Sent! ${data.push_sent} phone alert(s) delivered to ${data.subscribers} device(s)`);
      setTitle(""); setBody(""); load();
      window.dispatchEvent(new CustomEvent("i65:notif-refresh"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed to send"); }
    finally { setSending(false); }
  };
  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-notify">
      <div className="card-warm rounded-2xl p-6">
        <h2 className="headline text-2xl text-[#f7f3ec] mb-1">Send a Notification</h2>
        <p className="text-[#a9a196] text-sm mb-4">Members get an in-app alert instantly, plus a phone push if they've turned on alerts.</p>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Denim & Boots — Tickets Live!)" className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f] mb-3" data-testid="notify-title" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message" rows={4} className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f] mb-3" data-testid="notify-body" />
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <select value={audience} onChange={(e) => setAudience(e.target.value)} className="bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="notify-audience">
            <option value="members">All members</option>
            <option value="everyone">Members + admins</option>
          </select>
          <button onClick={send} disabled={sending} className="btn-gold rounded-full px-6 py-3 font-display uppercase tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="notify-send">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send
          </button>
        </div>
      </div>
      <div>
        <h3 className="font-display uppercase tracking-wide text-[#d6cec2] text-sm mb-3">Recent</h3>
        <div className="space-y-2">
          {history.length === 0 ? <p className="text-[#8f857a] text-sm">Nothing sent yet.</p> : history.map((n) => (
            <div key={n.id} className="card-warm rounded-xl p-4" data-testid="notify-history-item">
              <div className="flex justify-between gap-2">
                <p className="text-[#f7f3ec] font-semibold text-sm">{n.title}</p>
                <span className="text-[10px] uppercase tracking-wide text-[#6b645b] shrink-0 mt-0.5">{n.audience === "everyone" ? "Members + Admins" : "Members"}</span>
              </div>
              <p className="text-[#a9a196] text-sm mt-1">{n.body}</p>
              <p className="text-[#6b645b] text-[11px] mt-1">{new Date(n.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminReferral({ user }) {
  const origin = window.location.origin;
  const refUrl = `${origin}/events?ref=${user.ref_code}`;
  const joinUrl = `${origin}/join?ref=${user.ref_code}`;
  return (
    <div className="space-y-6" data-testid="admin-referral">
      <div className="card-warm rounded-2xl p-5 flex items-center justify-between">
        <div>
          <p className="text-[#a9a196] text-sm">Your referral code</p>
          <p className="headline text-3xl gold-text tracking-wide" data-testid="admin-ref-code">{user.ref_code}</p>
        </div>
        <Trophy className="text-[#d4af37]" size={36} />
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div className="card-warm rounded-2xl p-6">
          <h2 className="headline text-2xl text-[#f7f3ec] mb-1">My Ticket Link & QR</h2>
          <p className="text-[#a9a196] text-sm mb-4">Every ticket bought through this link is credited to you.</p>
          <div className="flex flex-col items-center gap-4">
            <div className="bg-white p-3 rounded-xl" data-testid="admin-ticket-qr"><QRCodeCanvas value={refUrl} size={140} /></div>
            <div className="w-full bg-[#1a1816] rounded-xl px-4 py-3 font-mono text-xs text-[#f3c649] break-all text-center" data-testid="admin-ticket-link">{refUrl}</div>
          </div>
          <div className="mt-4"><ShareMenu url={refUrl} label="Share your ticket link" /></div>
        </div>
        <div className="card-warm rounded-2xl p-6">
          <h2 className="headline text-2xl text-[#f7f3ec] mb-1 flex items-center gap-2"><UserPlus size={22} className="text-[#e0481f]" /> Recruit The Family</h2>
          <p className="text-[#a9a196] text-sm mb-4">Invite riders to join the I-65 Ryders. Your name rides along with the invite.</p>
          <div className="flex flex-col items-center gap-4">
            <div className="bg-white p-3 rounded-xl" data-testid="admin-join-qr"><QRCodeCanvas value={joinUrl} size={140} /></div>
            <div className="w-full bg-[#1a1816] rounded-xl px-4 py-3 font-mono text-xs text-[#f3c649] break-all text-center" data-testid="admin-join-link">{joinUrl}</div>
          </div>
          <div className="mt-4"><ShareMenu url={joinUrl} label="Share the invite" /></div>
        </div>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <Loader2 className="animate-spin text-[#e0481f]" />;
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={DollarSign} label="Event Revenue" value={`$${stats.total_revenue.toFixed(2)}`} />
        <Metric icon={Ticket} label="Tickets Sold" value={stats.total_tickets} />
        <Metric icon={CheckCircle2} label="Checked In" value={stats.checked_in} />
        <Metric icon={XCircle} label="Unused Tickets" value={stats.unused} />
      </div>

      <Panel title="Revenue By Event">
        {stats.by_event.map((e) => (
          <Row key={e.event_title} left={e.event_title} sub={`${e.tickets} tickets`} right={`$${e.revenue.toFixed(2)}`} />
        ))}
      </Panel>

      <Panel title="Member Sales Rankings" icon={Trophy}>
        {stats.member_rankings.length ? stats.member_rankings.map((m, i) => (
          <Row key={m.member_id} left={`#${i + 1} ${m.nickname}`} sub={`${m.tickets} tickets • ${m.orders} orders`} right={`$${m.revenue.toFixed(2)}`} highlight={i === 0} />
        )) : <p className="text-[#8f857a] text-sm">No member sales yet.</p>}
      </Panel>

      <Panel title="Online Sales (Not Attributed To A Member)">
        <Row left="Direct / Online" sub={`${stats.unattributed.tickets} tickets • ${stats.unattributed.orders} orders`} right={`$${stats.unattributed.revenue.toFixed(2)}`} />
      </Panel>
    </div>
  );
}

function CustomerExport() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/customers.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `i65-customers-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the customer list"); }
    setBusy(false);
  };
  return (
    <div className="bg-[#161412] rounded-2xl p-5 flex items-center gap-4 flex-wrap mb-2" data-testid="customer-export">
      <div className="flex-1 min-w-[200px]">
        <p className="headline text-lg text-[#f7f3ec]">Customer List</p>
        <p className="text-[#a9a196] text-sm">Every ticket, shop and square buyer with name, email and phone. Opens in Excel or Google Sheets.</p>
      </div>
      <button onClick={download} disabled={busy} className="btn-gold rounded-full px-5 py-2.5 font-display uppercase tracking-wide text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="customer-export-download">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Download
      </button>
    </div>
  );
}

function Orders() {
  const [view, setView] = useState("tickets");
  const pill = (id, label) => (
    <button onClick={() => setView(id)} className={`rounded-full px-4 py-2 font-display uppercase tracking-wide text-xs ${view === id ? "btn-gold" : "gold-border text-[#d6cec2]"}`} data-testid={`orders-view-${id}`}>{label}</button>
  );
  return (
    <div className="space-y-4">
      <CustomerExport />
      <div className="flex gap-2">{pill("tickets", "Ticket Orders")}{pill("shop", "Shop Orders")}</div>
      {view === "tickets" ? <TicketOrders /> : <ShopOrders />}
    </div>
  );
}

function ShopOrders() {
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/merch-orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, []);
  if (!orders) return <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-[#e0481f]" /></div>;
  if (!orders.length) return <p className="text-[#8f857a] text-sm" data-testid="shop-orders-empty">No shop orders yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-shop-orders">
      {orders.map((o) => (
        <div key={o.id} className="card-warm rounded-xl overflow-hidden">
          <button onClick={() => setOpen(open === o.id ? null : o.id)} className="w-full flex justify-between items-center px-5 py-4 text-left" data-testid={`shop-order-row-${o.id}`}>
            <div className="min-w-0">
              <p className="text-[#f7f3ec] font-medium truncate">{o.customer_name || "Customer"}</p>
              <p className="text-[#8f857a] text-xs truncate">{o.name}{o.size ? ` • ${o.size}` : ""} • {formatDate(o.created_at)}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="headline text-lg gold-text">${o.total}</p>
              <p className="text-xs font-display uppercase tracking-wide text-[#4ade80]">{o.status || "paid"} • {o.payment_method === "wallet_credit" ? "Wallet" : "Card"}</p>
            </div>
          </button>
          {open === o.id && (
            <div className="border-t border-[#3a332c] px-5 py-4 space-y-1.5 text-sm" data-testid={`shop-order-detail-${o.id}`}>
              <Detail k="Customer" v={o.customer_name || o.member_nickname || "—"} />
              <Detail k="Email" v={o.customer_email || "—"} />
              <Detail k="Phone" v={o.customer_phone || "—"} />
              <Detail k="Item" v={o.name} />
              <Detail k="Color" v={o.color || "—"} />
              <Detail k="Size" v={o.size || "—"} />
              <Detail k="Quantity" v={o.quantity} />
              <Detail k="Total" v={`$${o.total}`} />
              <Detail k="Purchase Date" v={formatDate(o.created_at)} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TicketOrders() {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/status`, { status }); toast.success(`Order ${status}`); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const resend = async (id) => {
    try { const { data } = await api.post(`/admin/orders/${id}/resend-confirmation`); toast.success(`Confirmation email sent to ${data.sent_to}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const statusColor = (s) => s === "refunded" ? "text-[#e0481f]" : s === "cancelled" ? "text-[#8f857a]" : "text-[#4ade80]";
  return (
    <div className="space-y-3" data-testid="admin-orders">
      {orders.map((o) => (
        <div key={o.id} className="card-warm rounded-xl overflow-hidden">
          <button onClick={() => setOpen(open === o.id ? null : o.id)} className="w-full flex justify-between items-center px-5 py-4 text-left" data-testid={`order-row-${o.id}`}>
            <div>
              <p className="text-[#f7f3ec] font-medium">{o.customer_name}</p>
              <p className="text-[#8f857a] text-xs">{o.event_title} • {formatDate(o.created_at)}</p>
            </div>
            <div className="text-right">
              <p className="headline text-lg gold-text">${o.amount_paid}</p>
              <p className={`text-xs font-display uppercase tracking-wide ${statusColor(o.status || "paid")}`}>{o.status || "paid"} • {o.member_nickname ? `via ${o.member_nickname}` : "Online"}</p>
            </div>
          </button>
          {open === o.id && (
            <div className="border-t border-[#3a332c] px-5 py-4 grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 text-sm">
                <Detail k="Customer" v={o.customer_name} />
                <Detail k="Email" v={o.customer_email} />
                <Detail k="Phone" v={o.customer_phone || "—"} />
                <Detail k="Event" v={o.event_title} />
                <Detail k="Ticket Type" v={o.ticket_type} />
                <Detail k="Amount Paid" v={`$${o.amount_paid}`} />
                <Detail k="Selling Member" v={o.member_nickname || "Online (unattributed)"} />
                <Detail k="Purchase Date" v={formatDate(o.created_at)} />
                <Detail k="Status" v={o.status || "paid"} />
                <div className="flex flex-wrap gap-2 pt-2">
                  <button onClick={() => setStatus(o.id, "paid")} className="rounded-full px-3 py-1.5 text-xs font-display uppercase tracking-wide border border-[#4ade80]/50 text-[#4ade80]" data-testid={`order-paid-${o.id}`}>Mark Paid</button>
                  <button onClick={() => setStatus(o.id, "cancelled")} className="rounded-full px-3 py-1.5 text-xs font-display uppercase tracking-wide border border-[#8f857a]/50 text-[#8f857a]" data-testid={`order-cancel-${o.id}`}>Cancel</button>
                  <button onClick={() => setStatus(o.id, "refunded")} className="rounded-full px-3 py-1.5 text-xs font-display uppercase tracking-wide border border-[#e0481f]/50 text-[#e0481f]" data-testid={`order-refund-${o.id}`}>Refund</button>
                  <button onClick={() => resend(o.id)} className="rounded-full px-3 py-1.5 text-xs font-display uppercase tracking-wide border border-[#d4af37]/60 text-[#f3c649]" data-testid={`order-resend-${o.id}`}>Resend Email</button>
                </div>
              </div>
              <div className="space-y-3">
                {o.tickets.map((t) => (
                  <div key={t.code} className="flex items-center gap-3 bg-[#1a1816] rounded-lg p-3">
                    <div className="bg-white p-1.5 rounded"><QRCodeCanvas value={t.code} size={54} /></div>
                    <div>
                      <p className="font-mono text-xs text-[#f3c649]">{t.code}</p>
                      <span className={`text-xs font-display uppercase tracking-wide ${t.checked_in ? "text-[#4ade80]" : "text-[#d4af37]"}`}>{t.checked_in ? "Checked In" : "Unused"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
      {orders.length === 0 && <p className="text-[#8f857a]">No orders yet.</p>}
    </div>
  );
}

function CheckIn() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  const doCheck = async (value) => {
    const c = (value || "").trim();
    if (!c) return;
    try {
      const { data } = await api.post("/admin/checkin", { code: c });
      setResult({ ok: true, ...data });
      toast.success(data.already_checked_in ? "Already checked in!" : "Checked in!");
    } catch (e) {
      setResult({ ok: false, msg: apiErr(e.response?.data?.detail) });
      toast.error("Invalid ticket");
    }
  };

  const scan = async () => { await doCheck(code); setCode(""); };

  const stopScan = useCallback(async () => {
    const s = scannerRef.current;
    if (s) {
      try { await s.stop(); await s.clear(); } catch (_) {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const startScan = () => {
    setResult(null);
    setScanning(true);
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decodedText) => { await stopScan(); await doCheck(decodedText); },
          () => {}
        );
      } catch (e) {
        toast.error("Could not access camera. Allow camera permission or type the code.");
        scannerRef.current = null;
        setScanning(false);
      }
    }, 120);
  };

  useEffect(() => () => { stopScan(); }, [stopScan]);

  return (
    <div className="max-w-md" data-testid="admin-checkin">
      <div className="card-warm rounded-2xl p-6">
        <QrCode className="text-[#e0481f] mb-3" size={40} />
        <h2 className="headline text-2xl text-[#f7f3ec] mb-1">Ticket Check-In</h2>
        <p className="text-[#a9a196] text-sm mb-4">Scan a ticket QR with your camera, or type the code.</p>
        {scanning ? (
          <div className="mb-4">
            <div id="qr-reader" className="rounded-xl overflow-hidden border border-[#3a332c] [&_video]:rounded-xl" data-testid="qr-camera" />
            <button onClick={stopScan} className="mt-3 w-full btn-outline-cream rounded-xl py-2.5 font-display uppercase tracking-wide text-sm" data-testid="checkin-stop-camera">Stop Camera</button>
          </div>
        ) : (
          <button onClick={startScan} className="w-full btn-gold rounded-xl py-3 font-display uppercase tracking-wide inline-flex items-center justify-center gap-2 mb-4" data-testid="checkin-start-camera"><Camera size={18} /> Scan With Camera</button>
        )}
        <div className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scan()} placeholder="I65-XXXXXXXX" className="flex-1 bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] font-mono placeholder:text-[#6b645b] focus:border-[#e0481f] outline-none" data-testid="checkin-code" />
          <button onClick={scan} className="btn-ember rounded-xl px-5 font-display uppercase tracking-wide" data-testid="checkin-submit">Check</button>
        </div>
      </div>
      {result && (
        <div className={`mt-4 rounded-2xl p-5 border ${result.ok ? "border-[#4ade80]/40 bg-[#4ade80]/5" : "border-[#e0481f]/40 bg-[#e0481f]/5"}`} data-testid="checkin-result">
          {result.ok ? (
            <>
              <div className="flex items-center gap-2 mb-2">{result.already_checked_in ? <XCircle className="text-[#d4af37]" /> : <CheckCircle2 className="text-[#4ade80]" />}<span className="headline text-xl text-[#f7f3ec]">{result.already_checked_in ? "Already Used" : "Valid Entry"}</span></div>
              <p className="text-[#d6cec2] text-sm">{result.customer_name} • {result.ticket_type}</p>
              <p className="text-[#8f857a] text-xs">{result.event_title}</p>
            </>
          ) : <p className="text-[#e0481f]">{result.msg}</p>}
        </div>
      )}
    </div>
  );
}

function Applications() {
  const [apps, setApps] = useState([]);
  const load = useCallback(() => api.get("/applications").then((r) => setApps(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { const { data } = await api.post(`/applications/${id}/approve`); toast.success(`Approved! Code: ${data.member_code}`); load(); };
  const deny = async (id) => { await api.post(`/applications/${id}/deny`); toast.success("Denied"); load(); };
  const createAcct = async (id) => {
    try {
      const { data } = await api.post(`/applications/${id}/create-account`);
      toast.success(`Account created • ${data.email} / ${data.temp_password}`, { duration: 10000 });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="admin-applications">
      {apps.map((a) => (
        <div key={a.id} className="card-warm rounded-xl p-5">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="headline text-xl text-[#f7f3ec]">{a.name}</h3>
              <p className="text-[#a9a196] text-sm">{a.email} {a.phone && `• ${a.phone}`}</p>
              <p className="text-[#d4af37] text-sm mt-1">Rides: {a.rides || "—"}</p>
              {a.bio && <p className="text-[#d6cec2] text-sm mt-2">{a.bio}</p>}
              {a.member_code && <p className="mt-2 font-mono text-xs text-[#f3c649] flex items-center gap-2">Code: {a.member_code} <button onClick={() => { navigator.clipboard.writeText(a.member_code); toast.success("Code copied"); }}><Copy size={13} /></button></p>}
            </div>
            <span className={`text-xs font-display uppercase tracking-wide px-3 py-1 rounded-full ${a.status === "approved" ? "bg-[#4ade80]/20 text-[#4ade80]" : a.status === "denied" ? "bg-[#e0481f]/20 text-[#e0481f]" : "bg-[#d4af37]/15 text-[#d4af37]"}`}>{a.status}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <a href={`mailto:${a.email}`} className="btn-outline-cream rounded-full px-4 py-2 text-sm font-display uppercase tracking-wide">Contact</a>
            {a.status !== "approved" && <button onClick={() => approve(a.id)} className="btn-gold rounded-full px-4 py-2 text-sm font-display uppercase tracking-wide" data-testid={`approve-${a.id}`}>Approve</button>}
            {a.status !== "denied" && <button onClick={() => deny(a.id)} className="rounded-full px-4 py-2 text-sm font-display uppercase tracking-wide border border-[#e0481f]/50 text-[#e0481f]" data-testid={`deny-${a.id}`}>Deny</button>}
            {a.status === "approved" && !a.member_created && <button onClick={() => createAcct(a.id)} className="btn-ember rounded-full px-4 py-2 text-sm font-display uppercase tracking-wide" data-testid={`create-acct-${a.id}`}>Create Account</button>}
          </div>
        </div>
      ))}
      {apps.length === 0 && <p className="text-[#8f857a]">No applications yet.</p>}
    </div>
  );
}

const emptyEvent = { title: "", subtitle: "", description: "", date: "", event_time: "", location: "", category: "Trail Ride", banner_image: "", flyer_image: "", ticket_types: [{ name: "General Admission", price: 25, description: "" }], published: true, featured: false };

function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get("/events?include_unpublished=true").then((r) => setEvents(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, date: new Date(form.date).toISOString(), ticket_types: form.ticket_types.map((t) => ({ name: t.name, price: parseFloat(t.price) || 0, description: t.description || "" })) };
      if (form.id) await api.put(`/events/${form.id}`, payload);
      else await api.post("/events", payload);
      toast.success("Event saved");
      setForm(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/events/${id}`); toast.success("Deleted"); load(); };

  if (form) return (
    <div className="card-warm rounded-2xl p-6 space-y-3 max-w-2xl" data-testid="event-form">
      <h2 className="headline text-2xl text-[#f7f3ec]">{form.id ? "Edit" : "New"} Event</h2>
      {["title", "subtitle", "location", "category", "event_time", "banner_image", "flyer_image"].map((k) => (
        <div key={k}><label className="eyebrow text-xs text-[#d4af37] block mb-1 capitalize">{k.replace("_", " ")}</label><input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="ainp" data-testid={`event-${k}`} /></div>
      ))}
      <div><label className="eyebrow text-xs text-[#d4af37] block mb-1">Date</label><input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="ainp" data-testid="event-date" /></div>
      <label className="flex items-center gap-2 text-[#f7f3ec] cursor-pointer"><input type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="event-featured" /> <span className="font-display uppercase tracking-wide text-sm">Featured on homepage (Next Event)</span></label>
      <div><label className="eyebrow text-xs text-[#d4af37] block mb-1">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="ainp resize-none" /></div>
      <div>
        <label className="eyebrow text-xs text-[#d4af37] block mb-1">Ticket Types</label>
        {form.ticket_types.map((t, i) => (
          <div key={i} className="flex flex-col gap-2 mb-3 border border-[#3a332c] rounded-xl p-3">
            <div className="flex gap-2">
              <input value={t.name} onChange={(e) => { const tt = [...form.ticket_types]; tt[i].name = e.target.value; setForm({ ...form, ticket_types: tt }); }} placeholder="Name" className="ainp flex-1" data-testid={`ticket-type-name-${i}`} />
              <input value={t.price} onChange={(e) => { const tt = [...form.ticket_types]; tt[i].price = e.target.value; setForm({ ...form, ticket_types: tt }); }} placeholder="Price" type="number" className="ainp w-24" data-testid={`ticket-type-price-${i}`} />
              <button onClick={() => setForm({ ...form, ticket_types: form.ticket_types.filter((_, x) => x !== i) })} className="text-[#e0481f]"><Trash2 size={16} /></button>
            </div>
            <input value={t.description || ""} onChange={(e) => { const tt = [...form.ticket_types]; tt[i].description = e.target.value; setForm({ ...form, ticket_types: tt }); }} placeholder="Description (optional) — e.g. Includes entry + 1 drink" className="ainp" data-testid={`ticket-type-desc-${i}`} />
          </div>
        ))}
        <button onClick={() => setForm({ ...form, ticket_types: [...form.ticket_types, { name: "", price: 0, description: "" }] })} className="text-[#f3c649] text-sm inline-flex items-center gap-1"><Plus size={14} /> Add ticket type</button>
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={save} className="btn-gold rounded-full px-6 py-2.5 font-display uppercase tracking-wide" data-testid="event-save">Save</button>
        <button onClick={() => setForm(null)} className="btn-outline-cream rounded-full px-6 py-2.5 font-display uppercase tracking-wide">Cancel</button>
      </div>
      <style>{`.ainp{width:100%;background:#1a1816;border:1px solid #3a332c;border-radius:10px;padding:9px 12px;color:#f7f3ec;outline:none}.ainp:focus{border-color:#e0481f}`}</style>
    </div>
  );

  return (
    <div data-testid="admin-events">
      <button onClick={() => setForm({ ...emptyEvent, date: new Date().toISOString().slice(0, 16) })} className="btn-ember rounded-full px-5 py-2.5 font-display uppercase tracking-wide mb-5 inline-flex items-center gap-2" data-testid="new-event"><Plus size={16} /> New Event</button>
      <div className="space-y-3">
        {events.map((e) => (
          <div key={e.id} className="card-warm rounded-xl p-4 flex items-center gap-4">
            <img src={e.banner_image} alt="" className="w-16 h-16 rounded-lg object-cover" />
            <div className="flex-1"><h3 className="headline text-lg text-[#f7f3ec]">{e.title}</h3><p className="text-[#8f857a] text-sm">{formatDate(e.date)}</p></div>
            <button onClick={() => setForm({ ...e, date: new Date(e.date).toISOString().slice(0, 16) })} className="btn-outline-cream rounded-full px-4 py-2 text-sm font-display uppercase" data-testid={`edit-event-${e.id}`}>Edit</button>
            <button onClick={() => del(e.id)} className="text-[#e0481f]"><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RewardsAdmin() {
  const [content, setContent] = useState(null);
  const [events, setEvents] = useState([]);
  const [contests, setContests] = useState([]);
  const [cf, setCf] = useState({ name: "", prize: "", event_id: "", start: "", end: "" });
  const loadC = useCallback(() => api.get("/admin/contests").then((r) => setContests(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/events?include_unpublished=true").then((r) => setEvents(r.data)).catch(() => {});
    loadC();
  }, [loadC]);
  if (!content) return <Loader2 className="animate-spin text-[#e0481f]" />;
  const tiers = content.commission_tiers || [];
  const setTier = (i, k, v) => { const t = [...tiers]; t[i] = { ...t[i], [k]: parseFloat(v) || 0 }; setContent({ ...content, commission_tiers: t }); };
  const saveTiers = async () => { await api.put("/content", { commission_tiers: content.commission_tiers }); toast.success("Commission tiers saved"); };
  const addTier = () => setContent({ ...content, commission_tiers: [...tiers, { min_sales: 0, percent: 15 }] });
  const delTier = (i) => setContent({ ...content, commission_tiers: tiers.filter((_, x) => x !== i) });
  const createContest = async () => {
    if (!cf.name || !cf.start || !cf.end) { toast.error("Name, start and end are required"); return; }
    try {
      await api.post("/admin/contests", { name: cf.name, prize: cf.prize, event_id: cf.event_id || null, start: new Date(cf.start).toISOString(), end: new Date(cf.end).toISOString() });
      toast.success("Contest created"); setCf({ name: "", prize: "", event_id: "", start: "", end: "" }); loadC();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delContest = async (id) => { await api.delete(`/admin/contests/${id}`); loadC(); };
  return (
    <div className="space-y-6" data-testid="admin-rewards">
      <div className="card-warm rounded-2xl p-5">
        <h3 className="headline text-xl text-[#f7f3ec] mb-1">Commission Tiers</h3>
        <p className="text-[#a9a196] text-sm mb-3">Members earn a higher % once their lifetime ticket sales cross each threshold.</p>
        {tiers.map((t, i) => (
          <div key={i} className="flex gap-2 mb-2 items-center">
            <span className="text-[#8f857a] text-sm">Sales ≥ $</span>
            <input type="number" value={t.min_sales} onChange={(e) => setTier(i, "min_sales", e.target.value)} className="rwinp w-28" data-testid={`tier-min-${i}`} />
            <span className="text-[#8f857a] text-sm">→</span>
            <input type="number" value={t.percent} onChange={(e) => setTier(i, "percent", e.target.value)} className="rwinp w-20" data-testid={`tier-pct-${i}`} />
            <span className="text-[#8f857a] text-sm">%</span>
            <button onClick={() => delTier(i)} className="text-[#e0481f]"><Trash2 size={16} /></button>
          </div>
        ))}
        <div className="flex gap-2 mt-2 items-center">
          <button onClick={addTier} className="text-[#f3c649] text-sm inline-flex items-center gap-1"><Plus size={14} /> Add tier</button>
          <button onClick={saveTiers} className="btn-gold rounded-full px-5 py-2 text-sm font-display uppercase tracking-wide ml-auto" data-testid="save-tiers">Save Tiers</button>
        </div>
      </div>
      <div className="card-warm rounded-2xl p-5">
        <h3 className="headline text-xl text-[#f7f3ec] mb-3">Sales Contests</h3>
        <div className="grid sm:grid-cols-2 gap-2 mb-3">
          <input value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} placeholder="Contest name" className="rwinp" data-testid="contest-name" />
          <input value={cf.prize} onChange={(e) => setCf({ ...cf, prize: e.target.value })} placeholder="Prize (e.g. $200 + trophy)" className="rwinp" data-testid="contest-prize" />
          <select value={cf.event_id} onChange={(e) => setCf({ ...cf, event_id: e.target.value })} className="rwinp" data-testid="contest-event"><option value="">All events</option>{events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}</select>
          <div />
          <div><label className="text-[#8f857a] text-xs">Start</label><input type="datetime-local" value={cf.start} onChange={(e) => setCf({ ...cf, start: e.target.value })} className="rwinp w-full" data-testid="contest-start" /></div>
          <div><label className="text-[#8f857a] text-xs">End</label><input type="datetime-local" value={cf.end} onChange={(e) => setCf({ ...cf, end: e.target.value })} className="rwinp w-full" data-testid="contest-end" /></div>
        </div>
        <button onClick={createContest} className="btn-ember rounded-full px-5 py-2.5 font-display uppercase tracking-wide inline-flex items-center gap-2" data-testid="create-contest"><Plus size={16} /> Create Contest</button>
        <div className="space-y-2 mt-4">
          {contests.map((c) => (
            <div key={c.id} className="flex justify-between items-center bg-[#1a1816] rounded-xl px-4 py-3" data-testid={`contest-row-${c.id}`}>
              <div><p className="text-[#f7f3ec]">{c.name}</p><p className="text-[#8f857a] text-xs">{formatDate(c.start)} → {formatDate(c.end)}{c.prize ? ` • ${c.prize}` : ""}</p></div>
              <button onClick={() => delContest(c.id)} className="text-[#e0481f]"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>
      <style>{`.rwinp{background:#1a1816;border:1px solid #3a332c;border-radius:10px;padding:8px 12px;color:#f7f3ec;outline:none}.rwinp:focus{border-color:#e0481f}`}</style>
    </div>
  );
}

function PayoutsAdmin() {
  const [payouts, setPayouts] = useState([]);
  const load = useCallback(() => api.get("/admin/payouts").then((r) => setPayouts(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/payouts/${id}/status`, { status }); toast.success(`Payout ${status}`); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const pending = payouts.filter((p) => p.status === "requested");
  return (
    <div data-testid="admin-payouts" className="space-y-3">
      <p className="text-[#a9a196] text-sm mb-2">{pending.length} pending cash-out request(s).</p>
      {payouts.map((p) => (
        <div key={p.id} className="card-warm rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`payout-row-${p.id}`}>
          <div><p className="text-[#f7f3ec] font-medium">{p.member_nickname}</p><p className="text-[#8f857a] text-xs">{formatDate(p.created_at)}</p></div>
          <p className="headline text-xl gold-text">${p.amount.toFixed(2)}</p>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-display uppercase tracking-wide ${p.status === "paid" ? "text-[#4ade80]" : p.status === "rejected" ? "text-[#e0481f]" : "text-[#d4af37]"}`}>{p.status}</span>
            {p.status === "requested" && <>
              <button onClick={() => setStatus(p.id, "paid")} className="rounded-full px-3 py-1.5 text-xs font-display uppercase tracking-wide border border-[#4ade80]/50 text-[#4ade80]" data-testid={`payout-paid-${p.id}`}>Mark Paid</button>
              <button onClick={() => setStatus(p.id, "rejected")} className="rounded-full px-3 py-1.5 text-xs font-display uppercase tracking-wide border border-[#e0481f]/50 text-[#e0481f]" data-testid={`payout-reject-${p.id}`}>Reject</button>
            </>}
          </div>
        </div>
      ))}
      {payouts.length === 0 && <p className="text-[#8f857a]">No cash-out requests yet.</p>}
    </div>
  );
}

function MembersAdmin() {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const origin = window.location.origin;

  const save = async (m) => {
    try {
      if (m._new) await api.post("/admin/members", { email: m.email, password: m.password || "ryder123", name: m.name, nickname: m.nickname, rides: m.rides, bio: m.bio, photo: m.photo, role: m.role || "member", socials: { instagram: m.instagram } });
      else await api.put(`/admin/members/${m.id}`, { email: m.email, name: m.name, nickname: m.nickname, rides: m.rides, bio: m.bio, photo: m.photo, role: m.role, ref_code: m.ref_code, socials: { instagram: m.instagram } });
      toast.success("Member saved"); setEditing(null); setCreating(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const resetPw = async (m) => {
    const pw = window.prompt(`New password for ${m.nickname || m.email}:`);
    if (!pw) return;
    try { await api.post(`/admin/members/${m.id}/password`, { password: pw }); toast.success("Password updated"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (m) => {
    if (!window.confirm(`Delete ${m.nickname || m.email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/members/${m.id}`); toast.success("Member deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const Editor = ({ m }) => {
    const [f, setF] = useState({ ...m, instagram: m.socials?.instagram || "" });
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    return (
      <div className="card-warm rounded-xl p-5 space-y-2" data-testid="member-editor">
        <div className="grid sm:grid-cols-2 gap-2">
          <input value={f.nickname || ""} onChange={set("nickname")} placeholder="Rider nickname" className="minp" data-testid="member-nickname" />
          <input value={f.name || ""} onChange={set("name")} placeholder="Real name" className="minp" data-testid="member-name" />
          <input value={f.email || ""} onChange={set("email")} placeholder="Email" className="minp" data-testid="member-email" />
          <input value={f.rides || ""} onChange={set("rides")} placeholder="What they ride" className="minp" data-testid="member-rides" />
          <input value={f.photo || ""} onChange={set("photo")} placeholder="Photo URL" className="minp" data-testid="member-photo" />
          <input value={f.instagram || ""} onChange={set("instagram")} placeholder="Instagram URL" className="minp" />
          {!f._new && <input value={f.ref_code || ""} onChange={set("ref_code")} placeholder="Ref code" className="minp" data-testid="member-refcode" />}
          <select value={f.role || "member"} onChange={set("role")} className="minp" data-testid="member-role"><option value="member">member</option><option value="admin">admin</option></select>
          {f._new && <input value={f.password || ""} onChange={set("password")} placeholder="Password (default ryder123)" className="minp" data-testid="member-password" />}
        </div>
        <textarea value={f.bio || ""} onChange={set("bio")} rows={2} placeholder="Bio" className="minp w-full resize-none" data-testid="member-bio" />
        <div className="flex gap-2">
          <button onClick={() => save(f)} className="btn-gold rounded-full px-5 py-2 font-display uppercase tracking-wide text-sm" data-testid="member-save">Save</button>
          <button onClick={() => { setEditing(null); setCreating(false); }} className="btn-outline-cream rounded-full px-5 py-2 font-display uppercase tracking-wide text-sm">Cancel</button>
        </div>
        <style>{`.minp{background:#1a1816;border:1px solid #3a332c;border-radius:10px;padding:9px 12px;color:#f7f3ec;outline:none;width:100%}.minp:focus{border-color:#e0481f}`}</style>
      </div>
    );
  };

  return (
    <div data-testid="admin-members" className="space-y-3">
      {creating ? <Editor m={{ _new: true, role: "member" }} /> : (
        <button onClick={() => setCreating(true)} className="btn-ember rounded-full px-5 py-2.5 font-display uppercase tracking-wide inline-flex items-center gap-2" data-testid="new-member"><Plus size={16} /> New Member</button>
      )}
      {members.map((m) => editing === m.id ? <Editor key={m.id} m={m} /> : (
        <div key={m.id} className="card-warm rounded-xl p-4 flex items-center gap-4" data-testid={`member-row-${m.ref_code}`}>
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#221f1c] shrink-0">{m.photo ? <img src={m.photo} alt="" className="w-full h-full object-cover" /> : null}</div>
          <div className="flex-1 min-w-0">
            <p className="text-[#f7f3ec] font-medium truncate">{m.nickname || m.name} {m.role === "admin" && <span className="text-[#f3c649] text-xs">• ADMIN</span>}</p>
            <p className="text-[#8f857a] text-xs truncate">{m.email} • {m.rides || "—"}</p>
            <button onClick={() => { navigator.clipboard.writeText(`${origin}/events?ref=${m.ref_code}`); toast.success("Referral link copied"); }} className="text-[#d4af37] text-xs font-mono inline-flex items-center gap-1 mt-0.5"><Copy size={11} /> {m.ref_code}</button>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => setEditing(m.id)} className="p-2 text-[#d6cec2] hover:text-[#f3c649]" title="Edit" data-testid={`edit-member-${m.ref_code}`}><Pencil size={16} /></button>
            <button onClick={() => resetPw(m)} className="p-2 text-[#d6cec2] hover:text-[#f3c649]" title="Reset password" data-testid={`pw-member-${m.ref_code}`}><KeyRound size={16} /></button>
            <button onClick={() => del(m)} className="p-2 text-[#e0481f]" title="Delete" data-testid={`del-member-${m.ref_code}`}><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function MerchAdmin() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", price: "", image: "", description: "", colors: "", sizes: "" });
  const load = useCallback(() => api.get("/merch").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.name || !f.image) { toast.error("Name and image required"); return; }
    const colors = f.colors.split(",").map((c) => c.trim()).filter(Boolean);
    const sizes = f.sizes.split(",").map((s) => s.trim()).filter(Boolean).map((label) => ({ label, upcharge: 0, in_stock: true }));
    await api.post("/merch", { name: f.name, price: parseFloat(f.price) || 0, image: f.image, description: f.description, colors, sizes });
    setF({ name: "", price: "", image: "", description: "", colors: "", sizes: "" }); toast.success("Added"); load();
  };
  const del = async (id) => { await api.delete(`/merch/${id}`); load(); };
  const feature = async (id) => { await api.post(`/merch/${id}/feature`); toast.success("Best Seller updated"); load(); };
  const toggleSize = async (id, label, inStock) => {
    await api.patch(`/merch/${id}/size`, { label, in_stock: inStock });
    toast.success(`${label} marked ${inStock ? "in stock" : "sold out"}`); load();
  };
  return (
    <div data-testid="admin-merch">
      <div className="card-warm rounded-2xl p-5 mb-6 grid sm:grid-cols-2 gap-2">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Product name" className="bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-name" />
        <input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="Price" type="number" className="bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-price" />
        <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
          <input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="Image URL (or upload →)" className="flex-1 min-w-[180px] bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-image" />
          <MediaUpload accept="image/*" label="Upload Photo" onUploaded={(u) => setF((prev) => ({ ...prev, image: u }))} />
        </div>
        {f.image && <img src={f.image} alt="preview" className="sm:col-span-2 h-28 w-28 object-cover rounded-xl border border-[#3a332c]" data-testid="merch-image-preview" />}
        <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" className="sm:col-span-2 bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-desc" />
        <input value={f.colors} onChange={(e) => setF({ ...f, colors: e.target.value })} placeholder="Colors (comma separated, optional)" className="bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-colors" />
        <input value={f.sizes} onChange={(e) => setF({ ...f, sizes: e.target.value })} placeholder="Sizes (comma separated, optional)" className="bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="merch-sizes" />
        <button onClick={add} className="btn-gold rounded-full px-6 py-3 font-display uppercase tracking-wide sm:col-span-2" data-testid="merch-add">Add Product</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {items.map((m) => (
          <div key={m.id} className="card-warm rounded-xl overflow-hidden relative group">
            <img src={m.image} alt="" className="w-full aspect-square object-cover" />
            {m.featured && <span className="absolute top-2 left-2 bg-[#e0481f] text-black text-[10px] font-display uppercase tracking-wider px-2 py-0.5 rounded-full">Best Seller</span>}
            <div className="p-3">
              <p className="text-[#f7f3ec] text-sm">{m.name}</p>
              <p className="gold-text headline text-lg">${m.price}</p>
              <button onClick={() => feature(m.id)} className={`mt-2 w-full rounded-full px-2 py-1.5 text-[11px] font-display uppercase tracking-wide inline-flex items-center justify-center gap-1 ${m.featured ? "bg-[#e0481f] text-black" : "gold-border text-[#f3c649] hover:bg-[#f3c649] hover:text-black"} transition-colors`} data-testid={`merch-feature-${m.id}`}>
                <Trophy size={12} /> {m.featured ? "Best Seller" : "Set as Best Seller"}
              </button>
              {m.sizes && m.sizes.length > 0 && (
                <div className="mt-2">
                  <p className="text-[#8f857a] text-[10px] uppercase tracking-wide mb-1">Tap a size to mark sold out</p>
                  <div className="flex flex-wrap gap-1" data-testid={`merch-stock-${m.id}`}>
                    {m.sizes.map((s) => (
                      <button key={s.label} onClick={() => toggleSize(m.id, s.label, !s.in_stock)}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-display uppercase border transition-colors ${s.in_stock ? "border-[#3a332c] text-[#f7f3ec] hover:border-[#e0481f]" : "border-transparent bg-[#2a1512] text-[#e0481f] line-through"}`}
                        title={s.in_stock ? "In stock — tap to mark sold out" : "Sold out — tap to restock"}
                        data-testid={`merch-stock-${m.id}-${s.label}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => del(m.id)} className="absolute top-2 right-2 bg-black/70 rounded-full p-1.5 text-[#e0481f] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`merch-del-${m.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      {items.length === 0 && <p className="text-[#8f857a]">No products yet. Add your first drop above.</p>}
    </div>
  );
}

function MediaUpload({ onUploaded, accept = "image/*", label = "Upload from device" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url, data.is_video);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept={accept} onChange={handle} className="hidden" data-testid="media-file-input" />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="btn-gold rounded-full px-6 py-3 font-display uppercase tracking-wide inline-flex items-center gap-2 disabled:opacity-60" data-testid="media-upload-btn">
        {busy ? <Loader2 className="animate-spin" size={16} /> : <UploadCloud size={16} />} {busy ? "Uploading…" : label}
      </button>
    </>
  );
}

function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const load = useCallback(() => api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const addMedia = async (url, isVideo) => {
    await api.post("/gallery", { media_url: url, caption, type: isVideo ? "video" : "image", is_video: !!isVideo });
    setCaption(""); toast.success("Added to gallery"); load();
  };
  const del = async (id) => { await api.delete(`/gallery/${id}`); load(); };
  return (
    <div data-testid="admin-gallery">
      <div className="card-warm rounded-2xl p-5 mb-6">
        <p className="text-[#a9a196] text-sm mb-3">Upload photos or videos straight from your phone or computer.</p>
        <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f] mb-3" data-testid="gallery-caption" />
        <div className="flex flex-wrap gap-2">
          <MediaUpload accept="image/*" label="Add Photo" onUploaded={(u) => addMedia(u, false)} />
          <MediaUpload accept="video/*" label="Add Video" onUploaded={(u, v) => addMedia(u, true)} />
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
        {items.map((g) => (
          <div key={g.id} className="relative aspect-square rounded-xl overflow-hidden group bg-black">
            {(g.is_video || g.type === "video")
              ? <video src={g.media_url} className="w-full h-full object-cover" muted playsInline />
              : <img src={g.media_url} alt="" className="w-full h-full object-cover" />}
            {(g.is_video || g.type === "video") && <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-display tracking-wide">VIDEO</span>}
            <button onClick={() => del(g.id)} className="absolute top-2 right-2 bg-black/70 rounded-full p-1.5 text-[#e0481f] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`gallery-del-${g.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnnouncementsAdmin() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState(""); const [body, setBody] = useState("");
  const load = useCallback(() => api.get("/announcements").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!title) return; await api.post("/announcements", { title, body }); setTitle(""); setBody(""); toast.success("Posted"); load(); };
  const del = async (id) => { await api.delete(`/announcements/${id}`); load(); };
  return (
    <div data-testid="admin-announcements">
      <div className="card-warm rounded-2xl p-5 mb-6 space-y-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid="ann-title" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message" rows={3} className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f] resize-none" data-testid="ann-body" />
        <button onClick={add} className="btn-gold rounded-full px-6 py-2.5 font-display uppercase tracking-wide" data-testid="ann-add">Post</button>
      </div>
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="card-warm rounded-xl p-4 flex justify-between items-start">
            <div><h3 className="headline text-lg text-[#f7f3ec]">{a.title}</h3><p className="text-[#d6cec2] text-sm">{a.body}</p></div>
            <button onClick={() => del(a.id)} className="text-[#e0481f]"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentAdmin() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/content").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return <Loader2 className="animate-spin text-[#e0481f]" />;
  const fields = [
    ["hero_welcome", "Hero — Small Top Line"], ["hero_headline", "Hero — Big Headline"], ["hero_subheadline", "Hero — Tagline"],
    ["hero_note", "Hero — Handwritten Note"], ["hero_image", "Hero — Background Image URL"],
    ["card_events_image", "Card Image — Events"], ["card_shop_image", "Card Image — Shop"], ["card_members_image", "Card Image — Members"], ["card_gallery_image", "Card Image — Gallery"],
    ["about_title", "About Title"], ["about_text", "About Text"],
    ["join_eyebrow", "Membership — Eyebrow"], ["join_headline", "Membership — Headline"], ["join_text", "Membership — Text"], ["membership_image", "Membership — Image URL"],
    ["instagram_handle", "Instagram Handle"], ["instagram_url", "Instagram URL"], ["facebook_url", "Facebook URL"], ["tiktok_url", "TikTok URL"], ["youtube_url", "YouTube URL"],
    ["membership_fee", "Membership Fee ($)"], ["membership_period", "Membership Period"], ["member_commission_percent", "Member Commission (%)"],
  ];
  const save = async () => { await api.put("/content", c); toast.success("Website content updated"); };
  return (
    <div className="card-warm rounded-2xl p-6 max-w-2xl space-y-3" data-testid="admin-content">
      {fields.map(([k, label]) => (
        <div key={k}>
          <label className="eyebrow text-xs text-[#d4af37] block mb-1">{label}</label>
          {k.includes("text") ? (
            <textarea value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} rows={3} className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f] resize-none" data-testid={`content-${k}`} />
          ) : (
            <input value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} className="w-full bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-3 text-[#f7f3ec] outline-none focus:border-[#e0481f]" data-testid={`content-${k}`} />
          )}
        </div>
      ))}
      <button onClick={save} className="btn-gold rounded-full px-6 py-3 font-display uppercase tracking-wide" data-testid="content-save">Save Changes</button>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="card-warm rounded-2xl p-5">
      <Icon className="text-[#e0481f] mb-2" size={22} />
      <p className="headline text-3xl text-[#f7f3ec]">{value}</p>
      <p className="text-[#8f857a] text-xs uppercase tracking-wide">{label}</p>
    </div>
  );
}
function Panel({ title, icon: Icon, children }) {
  return (
    <div className="card-warm rounded-2xl p-6">
      <h3 className="headline text-xl text-[#f7f3ec] mb-4 flex items-center gap-2">{Icon && <Icon size={18} className="text-[#d4af37]" />}{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ left, sub, right, highlight }) {
  return (
    <div className={`flex justify-between items-center rounded-xl px-4 py-3 ${highlight ? "bg-[#d4af37]/10 border border-[#d4af37]/30" : "bg-[#1a1816]"}`}>
      <div><p className="text-[#f7f3ec] text-sm font-medium">{left}</p>{sub && <p className="text-[#8f857a] text-xs">{sub}</p>}</div>
      <p className="headline text-lg gold-text">{right}</p>
    </div>
  );
}
function Detail({ k, v }) {
  return <div className="flex justify-between gap-4"><span className="text-[#8f857a]">{k}</span><span className="text-[#f7f3ec] text-right">{v}</span></div>;
}

function SquaresAdmin() {
  const [board, setBoard] = useState(null);
  const [meta, setMeta] = useState(null);
  const [edit, setEdit] = useState(null);
  const [editName, setEditName] = useState("");
  const [editSeller, setEditSeller] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [members, setMembers] = useState([]);
  const [wins, setWins] = useState({ q1: "", q2: "", q3: "", q4: "" });

  const load = useCallback(() => api.get("/admin/squares/board").then((r) => {
    if (r.data && r.data.id) { setBoard(r.data); setMeta((m) => m || r.data); }
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/members").then((r) => setMembers(r.data || [])).catch(() => {}); }, []);

  if (!board) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#e0481f]" /></div>;
  const m = meta || board;
  const boardLink = `${window.location.origin}/board`;

  const saveMeta = async () => {
    try {
      await api.put("/admin/squares/board", {
        title: m.title, team_a: m.team_a, team_b: m.team_b,
        price: parseFloat(m.price) || 0, payout_per_quarter: parseFloat(m.payout_per_quarter) || 0,
        cause: m.cause || "", status: m.status || "open",
      });
      toast.success("Board updated"); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setSquare = async (number, name) => {
    try { const r = await api.post("/admin/squares/square", { number, name: name || "", member_id: name ? (editSeller || null) : null, email: name && editEmail.trim() ? editEmail.trim() : null, phone: name ? editPhone.trim() : null }); setBoard(r.data); setEdit(null); toast.success(name ? `#${number} marked sold` : `#${number} reopened`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const draw = async (q) => {
    try { const r = await api.post("/admin/squares/draw", { quarter: q }); setBoard(r.data.board); toast.success(`${q.toUpperCase()} winner: #${r.data.number}`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setWinner = async (q, number) => {
    try { const r = await api.post("/admin/squares/winner", { quarter: q, number: number === "" || number == null ? null : parseInt(number) }); setBoard(r.data); toast.success("Winner updated"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setVideo = async (url) => {
    try { const r = await api.post("/admin/squares/video", { url: url || "" }); setBoard(r.data); setMeta(r.data); toast.success(url ? "Video posted" : "Video removed"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const quarters = [["q1", "1st"], ["q2", "2nd"], ["q3", "3rd"], ["q4", "4th"]];
  const winnerNums = Object.values(board.winners || {}).filter((n) => n != null);
  const inp = "bg-[#1a1816] border border-[#3a332c] rounded-xl px-4 py-2.5 text-[#f7f3ec] outline-none focus:border-[#e0481f] w-full";

  return (
    <div className="space-y-8" data-testid="squares-admin">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#1a1816] rounded-xl p-4 text-center"><p className="headline text-2xl gold-text">{board.stats.sold}</p><p className="text-[#8f857a] text-xs uppercase tracking-wide">Sold</p></div>
        <div className="bg-[#1a1816] rounded-xl p-4 text-center"><p className="headline text-2xl text-[#f7f3ec]">{board.stats.open}</p><p className="text-[#8f857a] text-xs uppercase tracking-wide">Open</p></div>
        <div className="bg-[#1a1816] rounded-xl p-4 text-center"><p className="headline text-2xl text-[#4ade80]">${board.stats.raised.toLocaleString()}</p><p className="text-[#8f857a] text-xs uppercase tracking-wide">Raised</p></div>
      </div>

      {/* Share link */}
      <div className="bg-[#161412] rounded-2xl p-5 space-y-3" data-testid="sq-share-link">
        <h3 className="headline text-lg text-[#f7f3ec] flex items-center gap-2"><Link2 size={18} className="text-[#f3c649]" /> Board Link For Customers</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="bg-[#1a1816] rounded-xl px-4 py-2.5 font-mono text-sm text-[#f3c649] break-all flex-1" data-testid="sq-share-url">{boardLink}</span>
          <button onClick={() => { navigator.clipboard.writeText(boardLink); toast.success("Board link copied"); }} className="btn-gold rounded-full px-5 py-2.5 font-display uppercase tracking-wide text-sm inline-flex items-center gap-1.5" data-testid="sq-share-copy"><Copy size={14} /> Copy</button>
        </div>
        <p className="text-[#8f857a] text-xs">Each member also has their own board link in their portal (ends in ?ref=THEIRCODE). Squares bought through it are credited to them below.</p>
        <BoardShareImage link={boardLink} />
      </div>

      {/* Sales by member */}
      <div className="bg-[#161412] rounded-2xl p-5 space-y-3" data-testid="sq-by-member">
        <h3 className="headline text-lg text-[#f7f3ec] flex items-center gap-2"><Users2 size={18} className="text-[#f3c649]" /> Squares Sold By Member</h3>
        {(board.by_member || []).length === 0 ? <p className="text-[#8f857a] text-sm">No member-credited squares yet.</p> : (
          <div className="space-y-2">
            {board.by_member.map((b) => (
              <div key={b.member_id} className="bg-[#1a1816] rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`sq-by-member-${b.member_id}`}>
                <span className="text-[#f7f3ec] font-display uppercase tracking-wide">{b.nickname || "Member"}</span>
                <span className="text-[#8f857a] text-xs truncate flex-1">#{b.squares.join(", #")}</span>
                <span className="headline text-xl gold-text">{b.squares.length}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="bg-[#161412] rounded-2xl p-5 space-y-3">
        <h3 className="headline text-lg text-[#f7f3ec]">Board Details</h3>
        <input value={m.title} onChange={(e) => setMeta({ ...m, title: e.target.value })} placeholder="Title" className={inp} data-testid="sq-title" />
        <div className="grid grid-cols-2 gap-3">
          <input value={m.team_a} onChange={(e) => setMeta({ ...m, team_a: e.target.value })} placeholder="Team A" className={inp} data-testid="sq-team-a" />
          <input value={m.team_b} onChange={(e) => setMeta({ ...m, team_b: e.target.value })} placeholder="Team B" className={inp} data-testid="sq-team-b" />
          <input value={m.price} onChange={(e) => setMeta({ ...m, price: e.target.value })} type="number" placeholder="Price per square" className={inp} data-testid="sq-price" />
          <input value={m.payout_per_quarter} onChange={(e) => setMeta({ ...m, payout_per_quarter: e.target.value })} type="number" placeholder="Payout per quarter" className={inp} data-testid="sq-payout" />
        </div>
        <textarea value={m.cause} onChange={(e) => setMeta({ ...m, cause: e.target.value })} rows={2} placeholder="Cause / description" className={`${inp} resize-none`} data-testid="sq-cause" />
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-[#f7f3ec] text-sm cursor-pointer">
            <input type="checkbox" checked={m.status === "open"} onChange={(e) => setMeta({ ...m, status: e.target.checked ? "open" : "closed" })} data-testid="sq-status" />
            Board open for sales
          </label>
          <button onClick={saveMeta} className="btn-gold rounded-full px-6 py-2.5 font-display uppercase tracking-wide text-sm ml-auto" data-testid="sq-save-meta">Save Details</button>
        </div>
      </div>

      {/* Numbers video */}
      <div className="bg-[#161412] rounded-2xl p-5 space-y-3">
        <h3 className="headline text-lg text-[#f7f3ec] flex items-center gap-2"><Video size={18} className="text-[#f3c649]" /> Numbers Draw Video</h3>
        <p className="text-[#a9a196] text-sm">Upload the video of the number draw — everyone sees it on the board page.</p>
        {board.numbers_video ? (
          <div className="space-y-2">
            <video src={board.numbers_video} controls className="w-full max-h-72 rounded-xl bg-black" data-testid="sq-video-preview" />
            <button onClick={() => setVideo("")} className="text-[#e0481f] text-sm inline-flex items-center gap-1" data-testid="sq-video-remove"><Trash2 size={14} /> Remove video</button>
          </div>
        ) : (
          <MediaUpload accept="video/*" label="Upload Video" onUploaded={(u) => setVideo(u)} />
        )}
      </div>

      {/* Winners */}
      <div className="bg-[#161412] rounded-2xl p-5 space-y-3">
        <h3 className="headline text-lg text-[#f7f3ec] flex items-center gap-2"><Trophy size={18} className="text-[#f3c649]" /> Quarter Winners</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {quarters.map(([q, label]) => (
            <div key={q} className="bg-[#1a1816] rounded-xl p-3 flex items-center gap-2 flex-wrap" data-testid={`sq-winner-row-${q}`}>
              <span className="text-[#d6cec2] text-sm w-16">{label} Q</span>
              <span className="headline text-lg gold-text w-16">{board.winners?.[q] != null ? `#${board.winners[q]}` : "—"}</span>
              <input value={wins[q]} onChange={(e) => setWins({ ...wins, [q]: e.target.value })} type="number" placeholder="#" className="bg-[#121110] border border-[#3a332c] rounded-lg px-2 py-1.5 text-[#f7f3ec] outline-none w-16 text-sm" data-testid={`sq-winner-input-${q}`} />
              <button onClick={() => setWinner(q, wins[q])} className="gold-border text-[#f3c649] rounded-lg px-3 py-1.5 text-xs font-display uppercase" data-testid={`sq-winner-set-${q}`}>Set</button>
              <button onClick={() => draw(q)} className="btn-gold rounded-lg px-3 py-1.5 text-xs font-display uppercase" data-testid={`sq-winner-draw-${q}`}>Draw</button>
              {board.winners?.[q] != null && <button onClick={() => setWinner(q, null)} className="text-[#e0481f]" data-testid={`sq-winner-clear-${q}`}><XCircle size={16} /></button>}
            </div>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div>
        <h3 className="headline text-lg text-[#f7f3ec] mb-1">Squares</h3>
        <p className="text-[#a9a196] text-sm mb-3">Tap a square to add a cash buyer's name or reopen it.</p>
        <div className="overflow-x-auto -mx-5 px-5 pb-2">
          <div className="grid grid-cols-10 gap-1.5 min-w-[560px]">
            {board.squares.map((sq) => {
              const isWinner = winnerNums.includes(sq.number);
              const cls = sq.status === "sold"
                ? "bg-gradient-to-br from-[#d4af37] to-[#b8860b] text-[#1a1208]"
                : sq.status === "pending" ? "bg-[#e0a23f]/15 border border-[#e0a23f]/40 text-[#e0a23f]"
                : "bg-[#1a1816] border border-[#d4af37]/25 text-[#d6cec2] hover:border-[#e0481f]";
              return (
                <button key={sq.number} onClick={() => { setEdit(sq.number); setEditName(sq.status === "sold" ? (sq.name || "") : ""); setEditSeller(sq.status === "sold" ? (sq.sold_by_id || "") : ""); setEditEmail(sq.status === "sold" ? (sq.email || "") : ""); setEditPhone(sq.status === "sold" ? (sq.phone || "") : ""); }}
                  className={`relative aspect-square rounded-lg flex flex-col items-center justify-center p-1 text-center transition-all ${cls} ${isWinner ? "ring-2 ring-[#c4473d]" : ""}`}
                  data-testid={`admin-square-${sq.number}`}>
                  <span className="absolute top-1 left-1.5 text-[9px] font-bold opacity-70">{sq.number}</span>
                  {isWinner && <Trophy size={10} className="absolute top-1 right-1 text-[#c4473d]" />}
                  <span className="text-[9px] sm:text-[11px] font-display font-bold leading-tight truncate max-w-full px-0.5">
                    {sq.status === "sold" ? (sq.name || "SOLD") : sq.status === "pending" ? "HELD" : ""}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Square editor modal */}
      {edit != null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-black/70 backdrop-blur-sm" onClick={() => setEdit(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-[#1a1816] border border-[#d4af37]/25 p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-square-modal">
            <h3 className="headline text-3xl text-[#f7f3ec]">Square #{edit}</h3>
            <p className="text-[#a9a196] text-sm mt-1 mb-4">Enter a buyer's name to mark it sold (cash buy-in), or leave blank and reopen.</p>
            <input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Buyer name" className={inp} data-testid="admin-square-name" autoFocus />
            <input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} type="email" placeholder="Buyer email (optional, for winner alert)" className={`${inp} mt-3`} data-testid="admin-square-email" />
            <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} type="tel" placeholder="Buyer phone (optional)" className={`${inp} mt-3`} data-testid="admin-square-phone" />
            <label className="block text-[#a9a196] text-xs uppercase tracking-wide mt-3 mb-1">Sold by member (optional)</label>
            <select value={editSeller} onChange={(e) => setEditSeller(e.target.value)} className={inp} data-testid="admin-square-seller">
              <option value="">No member / club</option>
              {members.map((mb) => <option key={mb.id} value={mb.id}>{mb.nickname || mb.name || mb.email}</option>)}
            </select>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setSquare(edit, editName.trim())} disabled={!editName.trim()} className="btn-gold rounded-full px-5 py-2.5 font-display uppercase tracking-wide text-sm flex-1 disabled:opacity-50" data-testid="admin-square-save">Mark Sold</button>
              <button onClick={() => { const sq = board.squares.find((s) => s.number === edit); if (sq?.status !== "open" && !window.confirm(`Reopen square #${edit}? This removes ${sq?.name || "the current buyer"} from it.`)) return; setSquare(edit, ""); }} className="btn-outline-cream rounded-full px-5 py-2.5 font-display uppercase tracking-wide text-sm" data-testid="admin-square-reopen">Reopen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
